import { useEffect, useRef } from 'react';
import type {
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';
import type { User } from '~hooks/useUsers/models';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `canPrefillSalesperson` flipping after mount, which only a record-company
 *           change does (membership, or that company's `require_salesperson`).
 * Writes:   `salespeople` — `[currentUser]` when it turns on, `[]` when it turns off.
 * Guards:   blank creates only (`!defaultOrder`, so `{ shouldDirty: false }` is safe);
 *           never touches a dirty field. Turning on fills only an empty field; turning
 *           off clears only a field holding exactly `[currentUser]`.
 * Legacy:   none.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("record company settings").
 */
export function useSalespersonPrefillSync({
  defaultOrder,
  canPrefillSalesperson,
  currentUser,
  getFieldState,
  getValues,
  setValue,
}: {
  defaultOrder: Order | null;
  canPrefillSalesperson: boolean;
  currentUser: User;
  getFieldState: UseFormGetFieldState<OrderFormV3FieldValues>;
  getValues: UseFormGetValues<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const previousCanPrefillRef = useRef(canPrefillSalesperson);

  useEffect(() => {
    if (previousCanPrefillRef.current === canPrefillSalesperson) return;
    previousCanPrefillRef.current = canPrefillSalesperson;
    if (defaultOrder || getFieldState('salespeople').isDirty) return;

    const salespeople = getValues('salespeople') ?? [];
    const holdsOnlyCurrentUser =
      salespeople.length === 1 && salespeople[0]?.id === currentUser.id;

    if (canPrefillSalesperson && salespeople.length === 0) {
      setValue('salespeople', [currentUser], { shouldDirty: false });
    } else if (!canPrefillSalesperson && holdsOnlyCurrentUser) {
      setValue('salespeople', [], { shouldDirty: false });
    }
  }, [
    canPrefillSalesperson,
    currentUser,
    defaultOrder,
    getFieldState,
    getValues,
    setValue,
  ]);
}
